import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { InfoButton } from "@vd/ui";
import { Check, CircleAlert } from "lucide-react";
import { useState } from "react";
import { errorMessage } from "../../api/client";
import { platform, qk, settingsQuery } from "../../api/ops";
import type { OpsPlatformSettings, OpsReadinessItem } from "../../api/types";
import { toast } from "../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Textarea,
} from "../../components/ui";
import { fmtDate } from "../../lib/format";

export const Route = createFileRoute("/_app/readiness")({
  staticData: { crumb: { label: "Readiness", parents: [] } },
  loader: ({ context }) => context.queryClient.prefetchQuery(settingsQuery),
  component: ReadinessPage,
});

/** The checklist rows, in the order of ADR 0054 (DESIGN.md "Platform providers"). */
export const READINESS_LABEL: Record<string, string> = {
  rmd: "Robocall Mitigation Database listing",
  stir_shaken: "STIR/SHAKEN by the carrier, in writing",
  form_499: "FCC Form 499 Filer ID",
  e911: "E911 and the 911 notice",
  cpni: "CPNI procedures",
  calea: "CALEA plan filed",
  taxes: "Taxes and fees",
  kyc: "KYC review and caller ID",
  traceback: "Traceback export",
  counsel: "Counsel sign-off",
};

type Confirm = "open" | "close" | "stop" | "resume" | null;

function ReadinessPage() {
  const qc = useQueryClient();
  const q = useQuery(settingsQuery);
  const [editing, setEditing] = useState<OpsReadinessItem | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const done = (s: OpsPlatformSettings, msg: string) => {
    qc.setQueryData(qk.settings, s);
    toast(msg);
  };
  const setting = useMutation({
    mutationFn: (c: Exclude<Confirm, null>) =>
      c === "open" || c === "close"
        ? platform.setSetting("outside_telephony", c === "open" ? "open" : "closed")
        : platform.setSetting("outbound_stop", c === "stop" ? "true" : "false"),
    onSuccess: (s, c) => {
      setConfirm(null);
      done(
        s,
        {
          open: "Outside calling is open",
          close: "Outside calling is closed",
          stop: "Stopped",
          resume: "Resumed",
        }[c],
      );
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const s = q.data;
  const allRecorded = s.readiness.every((r) => r.recorded_at);

  return (
    <>
      <PageHeader title="Readiness" />
      <div className="flex max-w-[880px] flex-col gap-5">
        <Card
          title={
            <span className="flex items-center gap-2">
              Outside calling and texting
              {s.outside_telephony_open ? <Badge tone="ok">Open</Badge> : <Badge>Closed</Badge>}
            </span>
          }
          actions={
            s.outside_telephony_open ? (
              <Button size="sm" onClick={() => setConfirm("close")}>
                Close outside calling
              </Button>
            ) : (
              <Button size="sm" variant="primary" disabled={!allRecorded} onClick={() => setConfirm("open")}>
                Open outside calling
              </Button>
            )
          }
        >
          <ul>
            {s.readiness.map((r) => (
              <li
                key={r.item}
                className="flex min-h-12 items-center gap-3 border-hairline border-b px-4 py-2 last:border-b-0"
              >
                {r.recorded_at ? (
                  <Check className="size-4 shrink-0 text-success" aria-label="Recorded" />
                ) : (
                  <CircleAlert className="size-4 shrink-0 text-ink-4" aria-label="Not done" />
                )}
                <span className="min-w-0 flex-1 truncate font-medium text-ui">
                  {READINESS_LABEL[r.item] ?? r.item}
                </span>
                <span className="shrink-0 text-caption text-ink-3">
                  {r.recorded_at ? `${fmtDate(r.recorded_at)} · ${r.recorded_by ?? ""}` : "Not done"}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
                  {r.recorded_at ? "Edit" : "Record"}
                </Button>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Emergency stop">
          <div className="flex min-h-12 items-center gap-3 px-4 py-2">
            <span className="flex min-w-0 flex-1 items-center gap-1.5 font-medium text-ui">
              Outbound for all companies
              <InfoButton kind="help" label="About the emergency stop">
                Stops outbound calls, texts and email sends for every company. 911 keeps working.
              </InfoButton>
            </span>
            {s.outbound_stop ? <Badge tone="danger">Stopped</Badge> : <Badge tone="ok">Running</Badge>}
            {s.outbound_stop ? (
              <Button size="sm" onClick={() => setConfirm("resume")}>
                Resume
              </Button>
            ) : (
              <Button size="sm" variant="danger" onClick={() => setConfirm("stop")}>
                Stop
              </Button>
            )}
          </div>
        </Card>
      </div>

      {editing ? (
        <RecordDialog item={editing} onClose={() => setEditing(null)} onSaved={(n) => done(n, "Recorded")} />
      ) : null}
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        size="sm"
        tone={confirm === "stop" ? "danger" : "default"}
        title={
          confirm === "open"
            ? "Open outside calling?"
            : confirm === "close"
              ? "Close outside calling?"
              : confirm === "stop"
                ? "Stop outbound for every company?"
                : "Resume outbound?"
        }
        description={
          confirm === "open"
            ? "Customer companies can call and text outside numbers once verified."
            : confirm === "close"
              ? "Customer companies stop calling and texting outside numbers. Internal and beta companies keep calling."
              : confirm === "stop"
                ? "Calls, texts and email sends stop now for every company. 911 keeps working."
                : "Calls, texts and email sends start again."
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant={confirm === "stop" ? "danger" : "primary"}
              busy={setting.isPending}
              onClick={() => confirm && setting.mutate(confirm)}
            >
              {confirm === "open"
                ? "Open"
                : confirm === "close"
                  ? "Close"
                  : confirm === "stop"
                    ? "Stop"
                    : "Resume"}
            </Button>
          </>
        }
      />
    </>
  );
}

function RecordDialog({
  item,
  onClose,
  onSaved,
}: {
  item: OpsReadinessItem;
  onClose: () => void;
  onSaved: (s: OpsPlatformSettings) => void;
}) {
  const [reference, setReference] = useState(item.reference ?? "");
  const [note, setNote] = useState(item.note ?? "");
  const save = useMutation({
    mutationFn: (clear: boolean) =>
      platform.record(item.item, clear ? null : reference.trim(), note.trim() || undefined),
    onSuccess: (s) => {
      onSaved(s);
      onClose();
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title={READINESS_LABEL[item.item] ?? item.item}
      footer={
        <>
          {item.recorded_at ? (
            <Button variant="ghost" className="mr-auto" onClick={() => save.mutate(true)}>
              Clear
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={!reference.trim()}
            onClick={() => save.mutate(false)}
          >
            Record
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Reference">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Note">
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
